/**
 * Meta Ads API service functions
 */

import type {
  MetaAuthorizeResponse,
  MetaConnectionResponse,
  MetaCampaignsResponse,
  MetaAdSetsResponse,
  MetaAdsResponse,
  MetaDateRange,
  ImportRequest,
  ImportResponse,
  ImportJob,
  MetaAccountsResponse,
} from '@/types/meta';

import { api } from './api';

/**
 * Get the current Meta connection for the team
 */
export async function getMetaConnection(teamId: string): Promise<MetaConnectionResponse> {
  return api.get<MetaConnectionResponse>('/meta/connections', { params: { teamId } });
}

/**
 * Get OAuth authorization URL for connecting Meta
 */
export async function getMetaAuthorizeUrl(teamId: string): Promise<MetaAuthorizeResponse> {
  return api.get<MetaAuthorizeResponse>('/meta/authorize', { params: { teamId } });
}

/**
 * Disconnect Meta account from team
 */
export async function disconnectMeta(teamId: string): Promise<{ message: string }> {
  return api.post<{ message: string }>('/meta/disconnect', undefined, { params: { teamId } });
}

// ============================================================================
// BROWSE API FUNCTIONS
// ============================================================================

/**
 * Get campaigns for the team's connected Meta ad account
 */
export async function getCampaigns(
  teamId: string,
  dateRange?: MetaDateRange
): Promise<MetaCampaignsResponse> {
  return api.get<MetaCampaignsResponse>('/meta/campaigns', {
    params: {
      teamId,
      ...(dateRange && { since: dateRange.since, until: dateRange.until }),
    },
  });
}

/**
 * Get ad sets for a specific campaign
 */
export async function getAdSets(
  teamId: string,
  campaignId: string,
  dateRange?: MetaDateRange
): Promise<MetaAdSetsResponse> {
  return api.get<MetaAdSetsResponse>('/meta/adsets', {
    params: {
      teamId,
      campaignId,
      ...(dateRange && { since: dateRange.since, until: dateRange.until }),
    },
  });
}

/**
 * Get ads for a specific ad set
 */
export async function getAds(teamId: string, adSetId: string): Promise<MetaAdsResponse> {
  return api.get<MetaAdsResponse>('/meta/ads', {
    params: { teamId, adSetId },
  });
}

// ============================================================================
// IMPORT API FUNCTIONS
// ============================================================================

/**
 * Queue import of selected ads
 * Returns 202 Accepted with job ID for polling
 */
export async function importAds(
  projectId: string,
  request: ImportRequest
): Promise<ImportResponse> {
  return api.post<ImportResponse>(`/projects/${projectId}/meta/import`, request);
}

/**
 * Get import job status
 */
export async function getImportJob(
  projectId: string,
  jobId: string
): Promise<ImportJob> {
  return api.get<ImportJob>(`/projects/${projectId}/meta/import-jobs/${jobId}`);
}

/**
 * List recent import jobs for project
 */
export async function listImportJobs(
  projectId: string
): Promise<{ importJobs: ImportJob[] }> {
  return api.get<{ importJobs: ImportJob[] }>(`/projects/${projectId}/meta/import-jobs`);
}

// ============================================================================
// ACCOUNT SELECTION API FUNCTIONS
// ============================================================================

/**
 * Get available ad accounts from pending session
 */
export async function getMetaAccounts(sessionId: string): Promise<MetaAccountsResponse> {
  return api.get<MetaAccountsResponse>('/meta/accounts', { params: { sessionId } });
}

/**
 * Finalize Meta connection with selected account
 */
export async function finalizeMetaConnection(
  sessionId: string,
  accountId: string
): Promise<MetaConnectionResponse> {
  return api.post<MetaConnectionResponse>('/meta/connections', {
    sessionId,
    accountId,
  });
}

/**
 * Cancel pending session (cleanup on user cancel)
 */
export async function cancelPendingSession(sessionId: string): Promise<{ message: string }> {
  return api.delete<{ message: string }>('/meta/pending-session', { params: { sessionId } });
}
